import { FC, useState } from "react";
import { Box, makeStyles } from "@material-ui/core";

import { EssentialImage, Image } from "components";
import { getThumbnailUrlFromMediaUrl } from "components/MediaPlayer";
import { ListingWithIncludes } from "services/listings/types";
import theme from "theme";
import { Theme } from "theme";

const thirdRowHeight = 360;
const maxYoutubeImgHeightSmDown = "500px";

const useStyles = makeStyles<
  Theme,
  {
    maxImageWidth: number;
  }
>({
  thirdRowImageContainer: {
    display: "flex",
    flexGrow: 8,
    alignItems: "center",
    maxWidth: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      justifyContent: "center",
    },
  },
  thirdRowImage: {
    borderRadius: theme.shape.borderRadius,
    maxHeight: thirdRowHeight,
    width: "100%",
    height: "auto",
    maxWidth: props => (!!props.maxImageWidth ? props.maxImageWidth : ""),
    [theme.breakpoints.down("sm")]: {
      maxHeight: "500px", // don't exceed 16:9 ratio at 895px (959px width - 2rem padding = 959 - 64 = 895px) * 9/16 = 503.4375px
      maxWidth: "100%",
    },
  },
  thirdRowYoutubeImageCrop: {
    position: "relative",
    width: "100%",
    paddingBottom: "56.25%", // 16:9 ratio (9/16 = 0.5625 or 56.25%)
    overflow: "hidden",
  },
  thirdRowYoutubeImage: {
    borderRadius: theme.shape.borderRadius,
    position: "absolute",
    top: "50%",
    left: 0,
    transform: "translate(0, -50%)",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    maxHeight: thirdRowHeight,
    maxWidth: `calc(${thirdRowHeight}px * 16/9)`,
    [theme.breakpoints.down("sm")]: {
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxHeight: maxYoutubeImgHeightSmDown,
      maxWidth: `calc(${maxYoutubeImgHeightSmDown} * 16/9)`,
    },
  },
});

interface HeaderImageProps {
  listing: ListingWithIncludes;
}

const HeaderImage: FC<HeaderImageProps> = ({ listing }) => {
  const [maxImageWidth, setMaxImageWidth] = useState(0);
  const { media_urls, thumbnail_large } = listing || {};

  const classes = useStyles({ maxImageWidth });

  const isMp4 = !!listing
    ? media_urls[0] && media_urls[0].includes(".mp4")
    : false;

  const youtubeThumbnail = !!listing
    ? getThumbnailUrlFromMediaUrl(media_urls[0], "maxresdefault")
    : "";

  const [youtubeThumbnailFallback, setYoutubeThumbnailFallback] = useState("");
  const handleYoutubeThumbnailError = () =>
    setYoutubeThumbnailFallback(
      getThumbnailUrlFromMediaUrl(media_urls[0], "hqdefault"),
    );

  return (
    <Box className={classes.thirdRowImageContainer}>
      {!!thumbnail_large ? (
        <Box>
          <EssentialImage>
            <img
              className={classes.thirdRowImage}
              src={thumbnail_large}
              onLoad={(e: any) => {
                const scaledWidth =
                  e.target.naturalWidth *
                  (thirdRowHeight / e.target.naturalHeight);
                setMaxImageWidth(scaledWidth);
              }}
            />
          </EssentialImage>
        </Box>
      ) : !!youtubeThumbnail ? (
        <Box className={classes.thirdRowImage}>
          <Box className={classes.thirdRowYoutubeImageCrop}>
            <EssentialImage>
              <img
                className={classes.thirdRowYoutubeImage}
                src={youtubeThumbnailFallback || youtubeThumbnail}
                onLoad={(e: any) => {
                  // the placeholder img served by youtube on a 404 is 90px tall
                  if (e.target.naturalHeight === 90) {
                    handleYoutubeThumbnailError();
                  }
                }}
                onError={handleYoutubeThumbnailError}
              />
            </EssentialImage>
          </Box>
        </Box>
      ) : isMp4 ? (
        <video
          className={classes.thirdRowImage}
          src={media_urls[0]}
          onLoadedData={(e: any) => {
            const isNarrow = e.target.videoHeight / e.target.videoWidth >= 1;
            const scaledWidth =
              e.target.videoWidth * (thirdRowHeight / e.target.videoHeight);
            setMaxImageWidth(isNarrow ? scaledWidth : 0);
          }}
          autoPlay
          loop
          muted
          playsInline
        />
      ) : (
        <Image className={classes.thirdRowImage} name="re-logo-white.svg" />
      )}
    </Box>
  );
};
export default HeaderImage;
